import { combineMode, convertUserCodeToGridCode } from "xlcommon/src/pyscript/utils";
import {
  runExcelSafeContext,
  splitFullAddress,
  worksheetIdFromSheetName,
  writeToCell,
  isPythonEnabled,
  writeRobustFormula,
} from "xlcommon/src/excel/excel-grid-utils";
import { PyScriptInterpreter, PyScriptLinkage, PyScriptOutput } from "xlcommon/src/pyscript/types";
import { PYTHON_SETTINGS_DEFINITION, PythonExecutionLocation, PythonSettings } from "./types";

async function writePyScriptCode(
  worksheetId: string,
  addr: string,
  interp: PyScriptInterpreter,
  userCode: string,
  mode: number
) {
  await runExcelSafeContext(async (context) => {
    const sheet = context.workbook.worksheets.getItem(worksheetId);
    const rng = sheet.getRange(addr);
    await writeRobustFormula(rng, convertUserCodeToGridCode(interp, userCode, mode, []), context);
    await context.sync();
  });
}

export async function writeRCode(fullAddress: string, data: any) {
  // Choose isolated mode since code does not depend on previously defined local variables.
  const mode = combineMode({ output: PyScriptOutput.Excel, linkage: PyScriptLinkage.Isolated });
  const [sheetName, addr] = splitFullAddress(fullAddress);
  const sheetId = await worksheetIdFromSheetName(sheetName);
  return await writePyScriptCode(sheetId, addr, PyScriptInterpreter.webr, data, mode);
}

export async function writeToExecutionLocation(fullAddress: string, data: any) {
  const executionLocation = await getPythonLocation();
  if (executionLocation === PythonExecutionLocation.Azure) {
    return await writeToCell(fullAddress, data);
  } else if (executionLocation === PythonExecutionLocation.Local) {
    // Choose isolated mode since code does not depend on previously defined local variables.
    const mode = combineMode({ output: PyScriptOutput.Excel, linkage: PyScriptLinkage.Isolated });
    const [sheetName, addr] = splitFullAddress(fullAddress);
    const sheetId = await worksheetIdFromSheetName(sheetName);
    return await writePyScriptCode(sheetId, addr, PyScriptInterpreter.pyodide, data, mode);
  }
}

export async function getPythonLocation(): Promise<PythonExecutionLocation> {
  return await runExcelSafeContext(async (context) => {
    const savedSettings = context.workbook.settings.getItemOrNullObject(PYTHON_SETTINGS_DEFINITION);
    savedSettings.load("value");
    await context.sync();

    if (savedSettings.isNullObject) {
      // Check if Python in Excel is enabled in the workbook
      if (await isPythonEnabled()) {
        console.log("No settings found - Defaulting to Azure (Python in Excel is available).");
        return PythonExecutionLocation.Azure;
      } else {
        console.log("No settings found - Defaulting to PyScript (Python in Excel is not available).");
        return PythonExecutionLocation.Local;
      }
    } else {
      const savedSettingsObject = JSON.parse(savedSettings.value);
      return savedSettingsObject.executionLocation as PythonExecutionLocation;
    }
  });
}

export async function savePythonSettings({ executionLocation }: PythonSettings) {
  await runExcelSafeContext(async (context) => {
    const serialized = JSON.stringify({ executionLocation });
    context.workbook.settings.add(PYTHON_SETTINGS_DEFINITION, serialized);
    await context.sync();
  });
}

export async function clearBindingDataFromExcel(bindId: string) {
  return await runExcelSafeContext(async (context) => {
    const binding = context.workbook.bindings.getItem(bindId);

    // Get the range associated with the binding
    const range = binding.getRange();

    // Clear the contents of the range / cell
    range.clear(Excel.ClearApplyTo.contents);

    await context.sync();
  });
}
